import { ecrireReglage, lireReglage } from '../noyau/db'
import { emettre } from '../noyau/bus'

/*
  Thème et gradateur.

  Le mode nuit commute un jeu de custom properties CSS sur
  :root[data-theme="rouge"]. Pas de filter: hue-rotate(), pas de invert() :
  le résultat serait visuellement faux et coûteux en rendu.

  Le gradateur n'utilise pas non plus filter: brightness(). Un filtre sur la
  racine force un calque de composition que le navigateur doit re-rastériser
  à chaque peinture — or le chrono repeint une fois par seconde, soit soixante
  repeints plein écran par minute sur un Android à 2 Go.

  À la place : un voile noir en opacity, composité par le GPU sans repeindre
  le contenu. Le rendu est IDENTIQUE, pas approchant — sur un fond noir,
  brightness(v) donne v × couleur, et un noir à l'alpha (1 − v) en source-over
  donne v × couleur + (1 − v) × 0. Même équation.
*/

export type Theme = 'normal' | 'rouge'

export const LUMINOSITE_MIN = 30
export const LUMINOSITE_MAX = 100
export const LUMINOSITE_DEFAUT = 70

let theme: Theme = 'normal'
let luminosite: number = LUMINOSITE_DEFAUT

export function themeCourant(): Theme {
  return theme
}

export function luminositeCourante(): number {
  return luminosite
}

function appliquer(): void {
  const racine = document.documentElement
  racine.setAttribute('data-theme', theme)

  // En mode normal, aucun voile : opacité nulle.
  const v = theme === 'rouge' ? luminosite / 100 : 1
  racine.style.setProperty('--voile-opacite', String(1 - v))

  const meta = document.querySelector('meta[name="theme-color"]')
  meta?.setAttribute('content', theme === 'rouge' ? '#080200' : '#0E0B09')
}

/**
 * Miroir synchrone pour le script anti-flash de index.html.
 * C'est la seule utilisation de localStorage du projet — IndexedDB est
 * asynchrone, donc inutilisable avant le premier rendu.
 */
function miroirSynchrone(): void {
  try {
    localStorage.setItem('kwata.theme', theme)
    localStorage.setItem('kwata.luminosite', String(luminosite))
  } catch {
    /* navigation privée ou quota plein : le thème reste correct pour la session */
  }
}

export async function initTheme(): Promise<void> {
  theme = (await lireReglage('theme')) ?? 'normal'
  luminosite = (await lireReglage('luminosite_nuit')) ?? LUMINOSITE_DEFAUT
  appliquer()
  miroirSynchrone()
}

export async function definirTheme(nouveau: Theme): Promise<void> {
  theme = nouveau
  appliquer()
  miroirSynchrone()
  await ecrireReglage('theme', theme)
  emettre('theme-change')
}

export async function basculerTheme(): Promise<void> {
  await definirTheme(theme === 'rouge' ? 'normal' : 'rouge')
}

export async function definirLuminosite(valeur: number): Promise<void> {
  luminosite = Math.min(LUMINOSITE_MAX, Math.max(LUMINOSITE_MIN, Math.round(valeur)))
  appliquer()
  miroirSynchrone()
  await ecrireReglage('luminosite_nuit', luminosite)
  emettre('theme-change')
}
